import type { RouteLoader } from './lazy-route';
import { prefetchChunk } from './stale-chunk';

/**
 * Loads each route's code one at a time while the browser is idle, so a later
 * click renders the page without waiting on the network. Returns a cancel
 * function.
 *
 * Skipped when the user has asked the browser to save data or is on a 2G
 * connection, where the downloads would compete with the page in use. Safari and
 * Firefox expose no `navigator.connection`, which reads as "no constraint".
 */
export function preloadRoutesWhenIdle(loaders: readonly RouteLoader[]): () => void {
  if (prefersReducedData()) return () => {};
  const queue = loaders.filter((loader) => !loader.loaded);
  let cancelled = false;
  let cancelPending = () => {};

  const next = () => {
    const loader = queue.shift();
    if (cancelled || !loader) return;
    // finally, not then: one failed chunk must not strand the rest of the queue.
    // A non-stale failure still rejects, and reaches Sentry like a failed hover prefetch.
    cancelPending = whenIdle(() => {
      void prefetchChunk(loader).finally(next);
    });
  };
  next();

  return () => {
    cancelled = true;
    cancelPending();
  };
}

function prefersReducedData(): boolean {
  const connection = navigator.connection;
  if (!connection) return false;
  return connection.saveData || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
}

// Safari has no requestIdleCallback; a short timeout still yields to the work
// the current page queued first.
function whenIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(callback);
    return () => window.cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(callback, 200);
  return () => window.clearTimeout(handle);
}
